import { DiaryStatus } from '../types/diaryStatus.type';
import { JobStatus } from '../types/jobStatus.type';
import { repairProgressColumn } from '../types/repairProgressColumn.type';
import { Action } from './action.model';
import { Authority } from './authority.model';
import { DocumentItem } from './document.model';
import { Gicop } from './gicop-notifications';
import { PreAuthority } from './preAuthority.model';
import { BasicQuotingSystem } from './user.model';

export interface Card {
  role: string;
  nameLine1?: string;
  nameLine2?: string;
  email?: string;
  workPhone?: string;
  mobilePhone?: string;
  homePhone?: string;
  postCode?: string;
}

export interface PhotoReference {
  id: number;
  extension: string;
  submitted: boolean;
  takenBy: string;
  captureTime: number;
  topImage: boolean;
  addedInV6: boolean;
  thumbNailUrl?: string;
}

export interface DocumentReference {
  id: number;
  extension: string;
  submitted: boolean;
  takenBy: string;
  filename: string;
  isFromEIM: boolean;
}

export interface Diary {
  id?: number;
  jobId: number;
  body: string;
  subject: string;
  sourceName?: string;
  createdTime?: number;
  date?: number;
  status?: DiaryStatus;
}

export interface Job {
  jobId: number;
  status: JobStatus;
  archived: boolean;
  repairerSiteId: number;
  editable: boolean;
  insco: string;
  requestForQuoteTimeStamp: number;
  rego: string;
  regoState: string;
  vehicleDescription: string;
  claimNumber: string;
  inspectionDate: string;
  inspectionType: string;
  onSiteInspection: boolean;
  logoUrl: string;
  product: string;
  repairerInstructions: string;
  descriptionIsEditable: boolean;
  lastAssessmentAction: string;
  lastReceivedDateTime: number;
  cards: Card[];
  documents: DocumentItem[];
  regoExpiryDate: string;
  odometer: number;
  vin: string;
  bodyColour: string;
  condition: string;
  complianceDate: string;
  incidentDate: string;
  accidentDescription: string;
  cancelled: boolean;
  unsubmittedDocument: boolean;
  noQuoteResponse: boolean;
  noQuoteResponseAssessmentAction: string;
  noQuoteResponseNote: string;
  booking: boolean;
  propositionDate: string;
  bookingOption: BookingOption;
  enableBooking: boolean;
  restrictInspectionDates: RestrictInspectionDates;
  excess: number;
  excessType: string;
  enableToSelfAuthorized: boolean;
  enableSelfAuthorityCurtain: boolean;
  vehicleDateIn: string;
  vehicleDateOut: string;
  externalCostControl: boolean;
  invoicedAndCostControlledExternally: boolean;
  lastExcessChangeTimestamp: number;
  pendingSupplement: boolean;
  totalLoss: boolean;
  unread: boolean;
  assessmentCategory: string;
  assessmentInstruction: string;
  accidentLocation: string;
  accessories: Accessory[];
  vehicleLocation: VehicleLocation;

  insurerId: number;
  insurerName: string;
  insurerAddress: string;
  insurerSuburb: string;
  insurerState?: string;
  insurerCity?: string;
  insurerPostCode?: string;
  insurerCountry: string;
  insurerEmail?: string;

  ownerPersonName?: string;
  ownerIsACompany: boolean;
  ownerCompanyName?: string;
  ownerContact?: string;
  ownerEmail?: string;
  ownerMobilePhone?: string;
  ownerWorkPhone?: string;
  ownerHomePhone?: string;

  claimHandlerPersonName?: string;
  claimHandlerPhone?: string;
  claimHandlerEmail?: string;

  assessingCompany?: string;
  assessorIsACompany?: boolean;
  assessorAllocated?: boolean;
  assessorCompanyName?: string;
  assessorContact?: string;
  assessorPersonName?: string;
  assessorEmail?: string;
  assessorMobilePhone?: string;
  assessorWorkPhone?: string;

  photoReferences: PhotoReference[];
  documentReferences: DocumentReference[];
  diaries: Diary[];
  unreadDiary: boolean;
  quotingSystem: BasicQuotingSystem;

  // VMD
  nvic: string;
  make: string;
  makeId: number;
  model: string;
  modelId: number;
  series: string;
  bodyType: string;
  doorCount: string;
  engine: string;
  transmission: string;
  vehicleYear: number;
  vehicleMonth: number;
  vehicleValue: number;
  authority: Authority;
  preAuthority: PreAuthority;
  unlistedGlassVehicle: boolean;

  ownerContribution: number;
  vehicleSelectedFromGlassGuide: boolean;

  action: Action;
  jobCanBeInvoiced: boolean;
  canRepairerUploadEstimatesAndInvoices?: boolean;

  authorityDate?: string;
  repairBookedDate?: string;
  repairStartedDate?: string;
  repairCompletedDate?: string;
  repairTimelineDate?: number;
  repairProgressColumn?: repairProgressColumn;
  repairerSiteName: string;
  selfAuthorized: boolean;
  allowSupplementQuote: boolean;

  // GICOP
  gicopUI: Gicop;
  photoIdentityUI?: PhotoIdentityUI;
  repairsCompletedFlag?: boolean;
  updateStrategyWaiting?: boolean;
  updateStrategy?: 'REPLACE' | 'MERGE';
}

export interface PhotoIdentityUI {
  preSignedUrl?: string;
  extension: string;
}

export type BookingOption = 'PROPOSE_ON_SITE_INSPECTION' | 'PROPOSE_NEW_DATE' | 'CANCELLED_DUE_TO_NO_SHOW';

export type RestrictInspectionDates = 'ACMS' | 'FREE_DATE';

export type VehicleLocation =
  | 'AT_REPAIRER'
  | 'AT_OWNERS_REGISTERED_ADDRESS'
  | 'AT_ACCIDENT_SITE'
  | 'AT_SALVAGE_YARD'
  | 'AT_TOWING_COMPANY'
  | 'UNKNOWN';

export const VEHICLE_LOCATION_OPTIONS: { value: VehicleLocation; label: string }[] = [
  { value: 'AT_REPAIRER', label: 'At Repairer' },
  { value: 'AT_OWNERS_REGISTERED_ADDRESS', label: "At Owner's Registered Address" },
  { value: 'AT_ACCIDENT_SITE', label: 'At Accident Site' },
  { value: 'AT_SALVAGE_YARD', label: 'At Salvage Yard' },
  { value: 'AT_TOWING_COMPANY', label: 'At Towing Company' },
  { value: 'UNKNOWN', label: 'Unknown' }
];

export interface Instant {
  seconds: number;
  nanos: number;
}

export interface RepairProgressBuckets {
  REPAIR_BOOKED: Job[];
  VEHICLE_IN: Job[];
  REPAIR_STARTED: Job[];
  REPAIR_COMPLETED: Job[];
  VEHICLE_OUT: Job[];
}

export interface Accessory {
  description: string;
  value: number;
}
